import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Megaphone,
  Settings,
  LogOut,
  CreditCard,
  Wallet,
  PieChart,
  Percent,
} from "lucide-react";
import { Button } from "../ui/button";
import { User } from "../../App";
import { DASHBOARD_ROUTES, LOCALSTORAGES } from "../../constants/constant";
import { toast } from "sonner";
import { useGetProfileApiCallQuery } from "../../services/api/auth/auth.api";
import { UserData } from "../../services/api/auth/auth.type";
import { useAppDispatch } from "../../hooks/reduxHooks";
import baseApi from "../../services/baseApi";

interface DashboardLayoutProps {
  // children: React.ReactNode;
  user: User | null;
  // onLogout: () => void;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ user }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: userProfile } = useGetProfileApiCallQuery();

  const navigation = [
    {
      name: "Dashboard",
      href: DASHBOARD_ROUTES.DASHBOARD,
      icon: LayoutDashboard,
    },
    { name: "Campaigns", href: DASHBOARD_ROUTES.CAMPAIGNS, icon: Megaphone },
    { name: "Users", href: DASHBOARD_ROUTES.USERS, icon: Users },
    { name: "KOL Management", href: DASHBOARD_ROUTES.KOL, icon: Users },

    {
      name: "Transactions",
      href: DASHBOARD_ROUTES.TRANSACTIONS,
      icon: CreditCard,
    },
    { name: "Wallets", href: DASHBOARD_ROUTES.WALLETS, icon: Wallet },
    {
      name: "Referrals Code",
      href: DASHBOARD_ROUTES.REFERRALS_CODE,
      icon: Percent,
    },

    {
      name: "Referrals Code Users",
      href: DASHBOARD_ROUTES.REFERRALS_CODE_USERS,
      icon: Percent,
    },

    // { name: "Financial", href: DASHBOARD_ROUTES.FINANCIAL, icon: PieChart },
    { name: "Settings", href: DASHBOARD_ROUTES.SETTINGS, icon: Settings },
  ];

  const handleLogout = () => {
    localStorage.removeItem(LOCALSTORAGES.TOKEN);
    dispatch(baseApi.util.resetApiState());
    toast.success("Logged out successfully");
    navigate("/login");
  };

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-sm border-r border-gray-200 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Airdrop Admin</h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            const isPermissionAllow =
              item.name.toLowerCase() === "dashboard" ||
              (userProfile?.result as UserData)?.permissions?.includes(
                item.name
              );

            if (userProfile?.result?.userType?.toLowerCase() === "admin") {
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            }

            return (
              isPermissionAllow && (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              )
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0) || "A"}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="p-2"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-auto">
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
